import { useEffect, useState } from "react";
import { Box, Typography, Select, MenuItem, Stack } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FOCUS_RING_SHADOW } from "@3sc/common-component";
import { FOOTER_YEAR } from "../../constant/constant";
import SCAILogo from "./SCAILogo";
import LangIcon from "./LangIcon";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { supportedLanguage, languageLabels } from "../../constant/constant";
import images from "../../constant/images";

/**
 * AuthShell
 * Shared outer layout used by all auth screens.
 * Matches Figma Splash-04: #EBEBF1 page bg → #EFEEF5 outer frame → #FFFFFF card.
 */
const AuthShell = ({ children, isLoading = false, isMac = false }) => {
  const [selectedLang, setSelectedLang] = useState(i18n.language || "en-US");
  const { t } = useTranslation();

  useEffect(() => {
    const onStorageChange = (event) => {
      if (event.key === "i18nextLng" && event.newValue) {
        i18n.changeLanguage(event.newValue);
      }
    };

    window.addEventListener("storage", onStorageChange);
    return () => {
      window.removeEventListener("storage", onStorageChange);
    };
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        background: "#EFEEF5",
        backgroundImage: ` radial-gradient(circle, rgba(7, 8, 11, 0.06) 0.095rem, transparent 0.095rem)`,
        backgroundSize: "1rem 1rem",
        backgroundRepeat: "repeat",
        overflow: "hidden", //  no scrollbars
        display: "flex",
        padding: "20px 30px",
      }}
    >
      {/* Outer background */}
      <Box
        sx={{
          background: "#EFEEF5",
          padding: "15px",
          border: "0.215rem solid #FFFFFF",
          boxShadow: "inset 0rem 0rem 0.3125rem rgba(0, 0, 0, 0.25)",
          borderRadius: "15px",
          '-electron-corner-smoothing': "90%",
          width: "100%",
          height: "100%",
          display: "flex",
        }}
      >
        {/* Inner white container */}
        <Box
          sx={{
            background: "#ffffff",
            borderRadius: "0.625rem",
            padding: "0.75rem",
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column", // 🔑 important
          }}
        >
          {/* 🔷 Top Bar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <SCAILogo isLoading={true} text={t("Thinking")}/>
            {/* <Box component="img" height="4rem" width="4.5rem" src={images.Animation} /> */}

            {/* Language picker */}
            <Stack direction="row" alignItems="center" spacing={0.1}>
              <LangIcon size={14} />
              <Select
                value={selectedLang || "en-US"}
                onChange={(e) => {
                  const selected = e.target.value;
                  i18n.changeLanguage(selected);
                  setSelectedLang(selected);
                }}
                variant="outlined"
                size="small"
                IconComponent={KeyboardArrowDownIcon}
                sx={{
                  fontFamily: "'Euclid Circular B', sans-serif",
                  fontSize: "1.143rem",
                  fontWeight: 400,
                  color: "#07080B",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  // The notched outline's border is suppressed above (always,
                  // even focused), so keyboard focus needs its own indicator.
                  outline: "none",
                  "&.Mui-focused": {
                    boxShadow: FOCUS_RING_SHADOW,
                  },
                  "& .MuiSelect-select": {
                    py: 0.4,
                    pr: "1.175 !important",
                    pl: 0.5,
                  },
                  "& .MuiSvgIcon-root": { fontSize: 14, color: "#07080B", right: "15px" },
                }}

                MenuProps={{
                  PaperProps: {
                    sx: {
                      boxShadow: "0px 0px 0px 0px rgba(0, 0, 0, 0.2), 0px 2px 7px 0px rgba(0, 0, 0, 0.14), 0px 3px 6px 0px rgba(0, 0, 0, 0.12)"
                    },
                  },
                }}
              >
                {supportedLanguage.map((lang) => (
                  <MenuItem
                    key={lang}
                    value={lang}
                    sx={{
                      fontSize: "1.143rem",
                    }}
                  >
                    {languageLabels[lang] || "Unknown"}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Box>

          {/* 🔷 Main Content */}
          <Box
            sx={{
              flex: "1 1 100%", //  fills space
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden", //  no scroll
              transform: isMac ? "scale(1.15)" : "scale(1)",
              // transformOrigin: "top center",
            }}
          >
            {children}
          </Box>

          {/* 🔷 Footer (NO absolute ) */}
          <Box
            sx={{
              mt: "auto", // 🔑 pushes to bottom
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: "0.375rem",
            }}
          >
            {/* 100% Data Security */}
            <Stack direction="row" alignItems="center" spacing={0.5} marginLeft={"0.625rem"}>
              <Box
                sx={{
                  width: "1.2rem",
                  cursor: "pointer",
                  transform: "rotate(90deg)",
                }}
                component="img"
                src={images.shieldTick}
              />
              <Typography
                sx={{
                  fontFamily: "'Euclid Circular B', sans-serif",
                  fontSize: "1rem",
                  fontWeight: 300,
                  lineHeight: "0.875rem",
                  color: "#4C4D4F",
                }}
              >
                {t("LABEL_DATA_SECURITY")}
              </Typography>
            </Stack>

            {/* Brand mark + copyright */}
            <Stack direction="row" alignItems="center" spacing={0.75} marginRight={"0.625rem"}>
              {/* Stacked rectangles logo */}
              <Box sx={{ position: "relative", width: "1rem", height: "0.90625rem" }}>
                <Box
                  sx={{
                    position: "absolute",
                    width: "0.845625rem",
                    height: "0.750625rem",
                    left: "0.154375rem",
                    top: "0.01625rem",
                    background: "#07080B",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    width: "0.610625rem",
                    height: "0.529375rem",
                    left: "0.07375rem",
                    top: "0.318125rem",
                    background: "#E94F1C",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    width: "0.375625rem",
                    height: "0.328125rem",
                    left: "0rem",
                    top: "0.5925rem",
                    background: "#07080B",
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: "'Euclid Circular B', sans-serif",
                  fontSize: "1rem",
                  fontWeight: 300,
                  lineHeight: "0.9375rem",
                  color: "#525252",
                }}
              >
                {t("© {{year}}", { year: FOOTER_YEAR })}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthShell;